import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import { Form } from "@/components/Form";
import { useFranqueados } from "@/hooks/franqueados";
import { Input, Select } from "@/components/Form";
import { Button } from "@/components/Common";
import { Row, Col } from "@/components/Grid";
import toastify from "@/services/toastify";

import { FormContainer, FormSection, LoadingWrapper } from "./styles";

export default function ContaBancaria() {
  const { data: user } = useSelector(state => state.auth);
  const {
    fetchContaBancaria,
    createContaBancaria,
    updateContaBancaria,
    fetchBancos
  } = useFranqueados();

  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBancos, setLoadingBancos] = useState(false);
  const [contaBancaria, setContaBancaria] = useState(null);
  const [formData, setFormData] = useState(null);

  const isEditing = !!contaBancaria;

  const tiposContaOptions = [
    { id: "CHECKING", title: "Conta Corrente" },
    { id: "SAVINGS", title: "Conta Poupança" },
    { id: "PAYMENT", title: "Conta Salário" }
  ];

  function handleOnChange(event) {
    const { name, value } = event.target;
    setFormData(state => ({ ...state, [name]: value }));
  }

  function getInitialData(conta) {
    if (conta) {
      return {
        codigo_banco: conta.codigo_banco,
        agencia: conta.agencia,
        conta_corrente: conta.conta_corrente,
        dv: conta.dv || "",
        tipo_conta: conta.tipo_conta
      };
    }
    return {
      codigo_banco: "",
      agencia: "",
      conta_corrente: "",
      dv: "",
      tipo_conta: "CHECKING"
    };
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadingBancos(true);
      try {
        const bancosData = await fetchBancos();
        if (!cancelled) {
          setBancos(
            bancosData
              .slice()
              .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
              .map(banco => ({
                id: banco.code,
                title: `${banco.code} - ${banco.short_name}`
              }))
          );
        }
      } catch (error) {
        if (!cancelled) toastify("Erro ao carregar lista de bancos", "error");
      } finally {
        if (!cancelled) setLoadingBancos(false);
      }

      if (!user?.perfil_id) return;
      try {
        const contas = await fetchContaBancaria(user.perfil_id);
        if (!cancelled) {
          const conta = contas?.[0] || null;
          setContaBancaria(conta);
          setFormData(getInitialData(conta));
        }
      } catch (error) {
        if (!cancelled) toastify("Erro ao carregar conta bancária", "error");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit() {
    setLoading(true);
    try {
      if (isEditing) {
        await updateContaBancaria(contaBancaria.id, formData);
      } else {
        const nova = await createContaBancaria(user.perfil_id, formData);
        setContaBancaria(nova);
      }
      toastify(
        isEditing
          ? "Conta bancária atualizada com sucesso!"
          : "Conta bancária cadastrada com sucesso!",
        "success"
      );
    } catch (error) {
      toastify(
        isEditing
          ? "Erro ao atualizar conta bancária"
          : "Erro ao cadastrar conta bancária",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }

  if (!formData || loadingBancos) {
    return (
      <FormContainer>
        <LoadingWrapper>
          <PulseLoader color="#FF987F" size={12} />
        </LoadingWrapper>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormSection>
        <Form onSubmit={handleSubmit} initialData={formData}>
          <Row hascols>
            <Col md="1/2">
              <Select
                name="codigo_banco"
                label="Banco *"
                options={bancos}
                loading={loadingBancos}
                placeholder="Selecione o banco"
                disabled={loadingBancos}
                value={formData.codigo_banco}
                onChange={handleOnChange}
              />
            </Col>
            <Col md="1/2">
              <Select
                name="tipo_conta"
                label="Tipo de Conta *"
                options={tiposContaOptions}
                placeholder="Selecione o tipo de conta"
                value={formData.tipo_conta}
                onChange={handleOnChange}
              />
            </Col>
          </Row>

          <Row hascols>
            <Col md="1/3">
              <Input
                name="agencia"
                label="Agência *"
                placeholder="Ex: 0001"
                maxLength="10"
                value={formData.agencia}
                onChange={handleOnChange}
              />
            </Col>
            <Col md="1/2">
              <Input
                name="conta_corrente"
                label="Número da Conta *"
                placeholder="Ex: 123456789"
                maxLength="20"
                value={formData.conta_corrente}
                onChange={handleOnChange}
              />
            </Col>
            <Col md="1/6">
              <Input
                name="dv"
                label="DV"
                placeholder="Ex: 1"
                maxLength="2"
                value={formData.dv}
                onChange={handleOnChange}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <div style={{ marginTop: "24px" }}>
                <Button
                  type="submit"
                  variant="cyan"
                  loading={loading}
                  disabled={loading || loadingBancos}
                >
                  {isEditing ? "Atualizar Conta" : "Cadastrar Conta"}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </FormSection>
    </FormContainer>
  );
}
